import { parseISO } from "date-fns";

import {
  AssetsPaginatedResponse,
  CompanyId,
  CursorState,
  ISO8601DateTimeString,
  LocationId,
  SelectivePartial,
  UserId,
  UUIDv4,
} from "../types";
import { Category } from "./categories";
import { ClassificationValueAssignment } from "./classifications";
import type { StatusCompletionValue } from "./completionTracking";
import {
  CustomField,
  CustomFieldDataValue,
  CustomFieldDTODataValue,
  CustomFieldFilters,
  Field,
  getCustomFieldDefaultValue,
  serializeCustomFieldValue,
  StandardField,
  StandardSortableField,
} from "./customFields";
import { PostBasedPaginatedResponse } from "./shared";
import { Status } from "./statusSets";
import { System } from "./systems";

// Canonical Asset shape — categoryId uses Category["internalId"] (numeric string),
// confirmed against assets-api-server's openapi/schemas/schemas.yaml
// (BaseAsset.categoryId example '42', no uuid format) and
// AssetBatchManager.validateCategoryIds. shared-web's prior GUID typing was a bug.
export type Asset = {
  barcode?: string;
  categoryId: Category["internalId"];
  // Reference-only, mirroring CategoryClassificationStructureAssignment: no node code/name is
  // ever stored or returned — resolve labels via acc-classifications' `nodeDisplay`/`treeInfo`
  // remotes. Populated only when the read was made with `includeClassificationValues=true`.
  classificationValues?: ClassificationValueAssignment[];
  clientAssetId: string;
  createdAt: Date;
  createdBy: UserId;
  companyId?: CompanyId;
  customAttributes?: Record<string, CustomFieldDataValue>;
  deletedAt?: Date;
  deletedBy?: UserId;
  description?: string | null;
  id: UUIDv4;
  isActive: boolean;
  locationId?: LocationId | null;
  // Per ILMARCH-61, asset-level percent-complete moved from a flat
  // `percentComplete: number` (0..100) to a nested map keyed by status id
  // with fractional values (0..1). Reads/writes route through the helpers
  // in completionTracking.ts so the conversion lives in exactly one place.
  statusCompletionsTracking?: Record<
    string,
    StatusCompletionValue | null
  > | null;
  statusId: Status["id"];
  updatedAt: Date;
  updatedBy: UserId;
  version: number;
  computedFields?: {
    parentSystemIds?: System["id"][];
  };
};

// For reference picker use
export type AssetWithCategoryAndStatus = Asset & {
  category?: Category;
  status?: Status;
};

export type AssetDuplicate = {
  categoryId: Category["internalId"];
  prevAssetTitle: Asset["clientAssetId"];
};

export type AssetDTO = Omit<
  Asset,
  "createdAt" | "updatedAt" | "deletedAt" | "customAttributes"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
  customAttributes?: Record<string, CustomFieldDTODataValue>;
};

export type NewAsset = SelectivePartial<
  Asset,
  "clientAssetId" | "statusId" | "categoryId",
  Exclude<AssetAttributeKey, AssetEditableAttributeKey>
> & {
  customAttributes?: Record<string, CustomFieldDataValue | undefined>;
};

export type NewAssetDTO = SelectivePartial<
  AssetDTO,
  "clientAssetId" | "statusId" | "categoryId",
  Exclude<AssetAttributeKey, AssetEditableAttributeKey | "customAttributes">
>;

// FIXME The types should require at least one change but `AtLeastOne` is producing
//  odd type errors that I don't have time to fix right now.
// Deliberately no `classificationValues` here: single-asset classification writes go through
// the dedicated `classification-values:batch-assign` endpoint, never a generic asset PATCH — see
// `BulkAssetUpdate` (shared-web) for the one place a classification write does ride a patch
// object (the bulk-edit job).
export type AssetUpdate = Partial<
  Pick<Asset, AssetEditableAttributeKey | "customAttributes">
>;

export type AssetAttributeKey = keyof Asset;

// Superset: app's includes statusCompletionsTracking (ILMARCH-61); shared-web's
// stale "id" member is dropped.
export type AssetEditableAttributeKey = Extract<
  AssetAttributeKey,
  | "clientAssetId"
  | "description"
  | "categoryId"
  | "locationId"
  | "statusId"
  | "barcode"
  | "statusCompletionsTracking"
>;

export type CategoryIdOrIds = Category["internalId"] | Category["internalId"][];

type AssetComputedFields = "parentSystemIds";

export type AssetFilters = {
  limit?: number;
  categoryId?: CategoryIdOrIds;
  // v1/REST-style filter — same internalId convention as Asset.categoryId
  // (corrects shared-web's prior GUID typing, per the resolved note above).
  categoryIds?: Category["internalId"][];
  assignedCategoryIds?: Category["internalId"][];
  statusLabels?: Status["label"][];
  statusIds?: Status["id"][];
  location?: {
    id: LocationId;
    includeSubLocations?: boolean;
  };
  customFields?: CustomFieldFilters;
  // Keyed by treeId to drive one filter block per structure; flattened to a single union of node
  // ids on the wire, since matching is OR across structures.
  classifications?: Record<string, string[]>;
  searchText?: string;
  includeCustomFields?: boolean;
  includeDeleted?: boolean;
  recoverable?: boolean;
  sourceModelLineageUrns?: string[];
  includedStandardFields?: StandardField["name"][];
  includedComputedFields?: AssetComputedFields[];
  parentSystemIds?: System["id"][];
  ancestorSystemIds?: System["id"][];
};

// Superset: app's adds categoryIdAndStatusId(WithSubtree)
export type AssetGroupBy =
  | "categoryId"
  | "categoryIdAndStatusId"
  | "categoryIdAndStatusIdWithSubtree"
  | "categoryIdWithSubtree"
  | "statusId"
  | "locationId";

export type ValueCountEntry<T = string> = {
  value: T; // Group by entity id
  count: number;
};

export type SingleGroupByAssetCount = ValueCountEntry;

export type MultipleGroupByAssetCount = {
  values: Record<string, string>; // Group by multiple entities' ids
  count: number;
};

export type AssetCount = SingleGroupByAssetCount | MultipleGroupByAssetCount;

export type AssetsCountResponse = AssetsPaginatedResponse<AssetCount[]> & {
  groupBy: AssetGroupBy;
};

export const parseAsset = ({
  createdAt,
  updatedAt,
  deletedAt,
  ...asset
}: AssetDTO): Asset => ({
  ...asset,
  createdAt: new Date(createdAt),
  updatedAt: new Date(updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
});

export const parseAssetList = (assets: AssetDTO[]): Asset[] =>
  assets.map(parseAsset);

export const serializeAssetCustomFields = (
  customFields: NonNullable<NewAsset["customAttributes"]>,
  customFieldNameMap: Record<CustomField["id"], CustomField>
): NonNullable<AssetDTO["customAttributes"]> =>
  Object.keys(customFields).reduce<NonNullable<AssetDTO["customAttributes"]>>(
    (parsedCustomAttributes, customFieldName) => {
      const customField = customFieldNameMap[customFieldName];

      if (customField) {
        const newValue = serializeCustomFieldValue(
          customField.dataType,
          customFields[customFieldName]
        );

        if (newValue !== undefined) {
          parsedCustomAttributes[customFieldName] = newValue;
        }
      }

      return parsedCustomAttributes;
    },
    {}
  );

export const serializeNewAsset = (
  { customAttributes, ...asset }: NewAsset,
  customFieldNameMap: Record<CustomField["id"], CustomField>
): NewAssetDTO => ({
  ...asset,
  ...(customAttributes && {
    customAttributes: serializeAssetCustomFields(
      customAttributes,
      customFieldNameMap
    ),
  }),
});

// `standardFieldMap` is app-only i18n/UI residue (see customFields.ts's T001.3.2
// split) and can't live in this pure lib, so it's injected here rather than
// imported directly — same pattern as parseCustomField's telemetry hook.
export const getFieldForKey = (
  key: string,
  customFieldNameMap: Record<CustomField["name"], CustomField>,
  // Defaulted so callers that only have a 2-arg reference to this function
  // (e.g. a plain re-export, not the app's injected wrapper) still compile
  // and degrade gracefully to `undefined` for standard fields instead of
  // erroring on a missing argument.
  standardFieldMap: Record<string, StandardSortableField> = {}
) => {
  switch (key) {
    case "barcode":
    case "categoryId":
    case "clientAssetId":
    case "locationId":
    case "description":
    case "statusId":
    case "createdAt":
    case "createdBy":
    case "updatedAt":
    case "updatedBy":
    case "deletedAt":
    case "deletedBy":
      return standardFieldMap[key];
    default:
      return customFieldNameMap[key];
  }
};

export const getAssetValue = (
  asset: Partial<Asset>,
  field: Field
): CustomFieldDataValue => {
  switch (field.dataType) {
    // Standard Fields
    case "barcode":
    case "category":
    case "id":
    case "location":
    case "description":
    case "status":
    case "userId":
      return (field.name in asset && asset[field.name]) || null;
    // Date fields, which can be standard or custom
    case "date": {
      if ("id" in field) {
        // Only custom fields have id, and only custom date fields need to be parsed
        // because standard date fields are parsed in parseAsset.
        // Standard date fields are in the form 2022-10-03T23:56:03.352Z
        // while custom date fields are in the form 2022-10-27.
        const customFieldValue =
          asset.customAttributes && asset.customAttributes[field.name];
        return customFieldValue && typeof customFieldValue === "string"
          ? parseISO(customFieldValue)
          : null;
      }
      // Standard date fields do not need to be parsed
      return (field.name in asset && asset[field.name]) || null;
    }
    // Custom Fields except date
    default: {
      const customFieldValue =
        asset.customAttributes && asset.customAttributes[field.name];

      return customFieldValue === undefined || customFieldValue === null
        ? null
        : customFieldValue;
    }
  }
};

export const produceAssetWithDefaultValues = (
  { customAttributes = {}, ...currentAsset }: Partial<NewAsset>,
  customFields: CustomField[] | undefined = []
) => {
  if (!Object.keys(customAttributes).length && !customFields.length) {
    return currentAsset;
  }

  const newAsset: Partial<NewAsset> = currentAsset;

  const newCustomAttributes = customFields.reduce<typeof customAttributes>(
    (newCustomAttributes, customField) => {
      if (
        customField.defaultValue === null ||
        customField.defaultValue === undefined ||
        customField.name in customAttributes // Don't update existing values
      ) {
        return newCustomAttributes;
      }

      newCustomAttributes[customField.name] =
        getCustomFieldDefaultValue(customField);

      return newCustomAttributes;
    },
    customAttributes
  );

  if (Object.keys(newCustomAttributes).length) {
    newAsset.customAttributes = newCustomAttributes;
  }

  return newAsset;
};

export interface AssetQuerySortOptions {
  // Not narrowed to `keyof Asset` — the app's grid can sort by a custom
  // field name too (see clients/helpers.ts#buildSortString), so this is a
  // plain wire-ready field name, not restricted to Asset's own keys.
  property: string;
  direction: "asc" | "desc";
}

export interface AssetQueryRequestOptions {
  includeDeleted?: boolean;
  includeTotalResults?: boolean;
  includedComputedFields?: string[];
  includedStandardFields?: string[];
}

// v2/POST-query filter — categoryIds here are GUID (Category["id"]), a
// different convention from AssetFilters.categoryIds above. Confirmed against
// assets-api-server's AssetsFilterSet vs AssetsFilterSetV2 schemas; both
// conventions are legitimately in use by their respective query discriminators.
export interface AssetQueryFilters {
  ids?: Asset["id"][];
  categoryIds?: Category["id"][];
  assignedCategoryIds?: Category["id"][];
  statusLabels?: Status["label"][];
  statusIds?: Status["id"][];
  locationIds?: Asset["locationId"][];
  customAttributes?: Asset["customAttributes"][];
  searchText?: string;
  sourceModelLineageUrns?: string;
  parentSystemIds?: System["id"][];
  ancestorSystemId?: System["id"][];
}

export interface AssetQueryRequestBody {
  entityType: "assets_atomic_query";
  includeFilters?: AssetQueryFilters;
  excludeFilters?: AssetQueryFilters;
  options: AssetQueryRequestOptions;
  orderBy: AssetQuerySortOptions;
}

export interface AssetQueryRequest {
  limit?: number;
  cursorState?: CursorState;
  query: AssetQueryRequestBody;
  options: AssetQueryRequestOptions;
  orderBy?: AssetQuerySortOptions[];
}

// Wraps AssetDTO (raw), not a parsed Asset — the backend returns plain
// ISO8601 strings with no pre-parsed variant.
export type AssetQueryResponse = PostBasedPaginatedResponse<
  AssetDTO,
  AssetQueryRequest
>;

// BulkAssetUpdate stays in shared-web/app (references Relationship from
// @adsk/acc-reference-picker, a UI/reference-picker dependency) — see the
// shared-web and app model shims for the residual definition.
